import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase, getSafeSession, waitForAuthInit } from '@/lib/supabaseClient';
import type { Profile, ProfileStatus } from '@/lib/supabaseClient';
import PendingApproval from './PendingApproval';

type AuthState = 'loading' | 'unauthed' | 'pending' | 'rejected' | 'approved';

export default function AuthGuard({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>('loading');
  const [profile, setProfile] = useState<Profile | null>(null);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    let authReady = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

    function resolveState() {
      getSafeSession()
        .then((session) => {
          if (!mounted) return;
          const userId = session?.user?.id;
          if (!userId) {
            if (fallbackTimer) clearTimeout(fallbackTimer);
            setState('unauthed');
            return;
          }
          supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .maybeSingle()
            .then(({ data }) => {
              if (!mounted) return;
              if (fallbackTimer) clearTimeout(fallbackTimer);
              const p = data as Profile | null;
              setProfile(p);
              const status: ProfileStatus = p?.status ?? 'pending';
              if (status === 'approved') setState('approved');
              else if (status === 'rejected') setState('rejected');
              else setState('pending');
            })
            .catch(() => {
              if (!mounted) return;
              if (fallbackTimer) clearTimeout(fallbackTimer);
              setState('pending');
            });
        })
        .catch(() => {
          if (!mounted) return;
          if (fallbackTimer) clearTimeout(fallbackTimer);
          setState('unauthed');
        });
    }

    // 저장된 세션 복구(토큰 검증)가 끝난 뒤에 인증 상태를 판단한다.
    // 그전에는 INITIAL_SESSION(null) 이벤트가 와도 무시해서, 복구 중인 세션을
    // '로그아웃'으로 잘못 해석해 로그인 화면으로 튕기는 것을 막는다.
    waitForAuthInit()
      .then(() => {
        if (!mounted) return;
        authReady = true;
        resolveState();
        fallbackTimer = setTimeout(() => {
          if (mounted) setState('unauthed');
        }, 3000);
      })
      .catch(() => {
        if (!mounted) return;
        authReady = true;
        setState('unauthed');
      });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted || !authReady) return;
      if (!session) {
        if (fallbackTimer) clearTimeout(fallbackTimer);
        setState('unauthed');
        return;
      }
      resolveState();
    });

    return () => {
      mounted = false;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      listener.subscription.unsubscribe();
    };
  }, []);

  if (state === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center bg-background-50">
        <div className="flex items-center gap-3 text-foreground-600">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
          <span className="text-sm">불러오는 중...</span>
        </div>
      </div>
    );
  }

  if (state === 'unauthed') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (state === 'pending' || state === 'rejected') {
    return <PendingApproval status={state} profile={profile} />;
  }

  return <>{children}</>;
}